import React, { useState, useImperativeHandle, useEffect } from "react";
import { Toast, Loading } from "@xp-design/mobile";
import PopupCom from "@/component/popupCom";
import { getActivityInfo } from "@/api/configPage";
import { trackGdp } from "@/utils/gio-report";
import { getChannel } from "@/utils/tools";
import "./index.less";

interface Props {
  // 组件不需要额外的props，所有配置都在PopupCom内部处理
}

export interface ActivityDetailPopupRef {
  // 打开弹窗 - 通过activityCode获取详情
  openWithActivityCode: (activityCode: string, activityInfo?: any) => void;
  // 打开弹窗 - 直接传入活动描述
  openWithDescription: (
    activityDescribe: string,
    activityId?: string,
    activityInfo?: any,
  ) => void;
  // 关闭弹窗
  close: () => void;
}

const ActivityDetailPopup = React.forwardRef<ActivityDetailPopupRef, Props>(
  (props, ref) => {
    const [desc, setDesc] = useState<React.ReactNode>();
    const popupRef = React.useRef<any>();

    useEffect(() => {
      // 保留原始页面的标题设置功能（但在弹窗中不需要）
      // document.title = "活动详情";
    }, []);
    // 通过activityCode获取活动详情（与 activityDetail 页面保持一致）
    const fetchActivityDetail = async (
      activityCode: string,
      activityInfo?: any,
    ) => {
      try {
        Loading.show("加载中...");
        const res = await getActivityInfo({ activityCode });

        if (res.code === 200) {
          // 埋点上报（与 activityDetail 页面完全一致）
          trackGdp("P20191", "B001", {
            trackName: "充电_营销活动详情_页面访问",
            origin: getChannel(),
            userType: "",
            activityId: activityCode, // 与原始页面保持一致，传入 activityCode
          });

          // 完全采用 activityDetail 页面的逻辑
          let originalText = res.data?.lotteryActivityDto?.activityDescribe;
          const formattedText = originalText?.split("\n").map((line, index) => (
            <div
              key={index}
              style={{
                // textIndent: "2em", // 保留原始注释，与原页面一致
                textAlign: "justify", // 启用两端对齐
                textJustify: "inter-character", // 优化中文对齐
                wordBreak: "break-all", // 强制长单词换行
                maxWidth: "100%", // 固定容器宽度（按需调整）
                margin: "0 auto", // 居中显示（可选）
              }}
            >
              {line}
            </div>
          ));
          setDesc(formattedText);
        } else {
          Toast.show({ content: res.msg });
        }
      } catch (error: any) {
        // 与 activityDetail 页面保持一致：catch 块为空
      } finally {
        Loading.hide();
      }
    };

    // 直接设置活动描述（简化版）
    const setActivityDescription = (
      activityDescribe: string,
      activityId?: string,
      activityInfo?: any,
    ) => {
      // 埋点上报（与原始页面保持一致）
      trackGdp("P20191", "B001", {
        trackName: "充电_营销专题页_页面访问",
        origin: getChannel(),
        activityId: activityId || "",
      });

      // 直接采用原始页面的格式化逻辑
      const formattedText = activityDescribe?.split("\n").map((line, index) => (
        <div
          key={index}
          style={{
            textAlign: "justify",
            textJustify: "inter-character",
            wordBreak: "break-all",
            maxWidth: "100%",
            margin: "0 auto",
          }}
        >
          {line}
        </div>
      ));
      setDesc(formattedText);
    };

    // 暴露方法给父组件
    useImperativeHandle(ref, () => ({
      openWithActivityCode: (activityCode: string, activityInfo?: any) => {
        fetchActivityDetail(activityCode, activityInfo);
        popupRef.current?.onOpen();
      },
      openWithDescription: (
        activityDescribe: string,
        activityId?: string,
        activityInfo?: any,
      ) => {
        setActivityDescription(activityDescribe, activityId, activityInfo);
        popupRef.current?.onOpen();
      },
      close: () => {
        popupRef.current?.onClose();
      },
    }));

    // 弹窗关闭回调
    const handleClose = () => {
      setDesc(null);
    };

    return (
      <PopupCom
        ref={popupRef}
        title="活动详情"
        height="80vh"
        bacUrl="" // 与原始页面保持一致，不使用背景图
        onCloseCallBack={handleClose}
      >
        <div className="activity-detail-popup-scroll">
          <div className="activity-detail-popup-content">{desc}</div>
        </div>
      </PopupCom>
    );
  },
);

export default ActivityDetailPopup;
